import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Loader2,
  ExternalLink,
  AlertTriangle,
  Sparkles,
  Flag,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const WRITE_ROLES = ["owner", "admin", "researcher"];

const SENTIMENT_BADGE = {
  positive: "border-emerald-200 bg-emerald-50 text-emerald-700",
  negative: "border-rose-200 bg-rose-50 text-rose-700",
  neutral: "border-slate-200 bg-slate-50 text-slate-700",
  mixed: "border-amber-200 bg-amber-50 text-amber-800",
};

function confidenceBand(s) {
  if (s >= 0.75) return "high";
  if (s >= 0.5) return "medium";
  return "low";
}

export default function FindingDetail() {
  const { t } = useTranslation(["findings", "common", "errors"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flagging, setFlagging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/findings/${id}`);
      setData(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(t("errors:notFound"));
        navigate("/findings");
        return;
      }
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => { load(); }, [load]);

  const flagEvidence = async (evidenceId) => {
    setFlagging(true);
    try {
      await api.post(`/evidence/${evidenceId}/flag`, { reason: "irrelevant" });
      toast.success(t("findings:detail.back"));
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setFlagging(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
      </div>
    );
  }

  const f = data.finding;
  const band = confidenceBand(f.confidence);
  const confCls =
    band === "high" ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
    band === "medium" ? "border-sky-200 bg-sky-50 text-sky-700" :
    "border-amber-200 bg-amber-50 text-amber-800";

  return (
    <div className="space-y-8" data-testid="finding-detail-page">
      <Link to="/findings" className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900" data-testid="finding-detail-back">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> {t("findings:detail.back")}
      </Link>

      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">
            <Sparkles className="mr-1 h-3 w-3" strokeWidth={2} />
            {t(`type.${f.type}`)}
          </Badge>
          <span className="text-sm text-slate-500">{f.theme_label}</span>
          <Badge variant="outline" className={confCls}>
            {band === "low" && f.confidence < 0.6 ? t("confidence.lowBadge") : t(`confidence.${band}`)} · {Math.round((f.confidence || 0) * 100)}%
          </Badge>
        </div>
        <h1 className="font-display text-2xl font-semibold leading-snug tracking-tight text-slate-900">
          {f.text}
        </h1>
        {f.recommended_action && (
          <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">{t("card.recommendedAction")}</p>
            <p className="mt-1 text-sm text-amber-900">{f.recommended_action}</p>
          </div>
        )}
      </header>

      {f.confidence_components && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {Object.entries(f.confidence_components).map(([k, v]) => (
            <div key={k} className="rounded-lg bg-slate-50 px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{t(`confidence.components.${k}`)}</p>
              <p className="mt-1 font-mono text-xs font-semibold text-slate-900">{Math.round((v || 0) * 100)}%</p>
            </div>
          ))}
        </div>
      )}

      {/* Supporting evidence */}
      <section className="space-y-3" data-testid="finding-supporting-section">
        <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">{t("findings:detail.supportingEvidenceTitle")}</h2>
        {(data.evidence || []).length === 0 ? (
          <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500">{t("findings:detail.noEvidence")}</p>
        ) : (
          <ul className="space-y-2">
            {data.evidence.map((e) => (
              <li key={e.id} data-testid={`finding-evidence-${e.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm text-slate-900">&quot;{e.quote}&quot;</p>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    {e.source_url && (
                      <a href={e.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-700 hover:underline">
                        {(e.source_title || e.source_url).slice(0, 50)}
                        <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                      </a>
                    )}
                    <Badge variant="outline" className={SENTIMENT_BADGE[e.sentiment] || SENTIMENT_BADGE.neutral}>{e.sentiment}</Badge>
                    <span className="text-slate-500">rel {Math.round((e.source_reliability || 0) * 100)}%</span>
                  </div>
                  {canWrite && !e.flagged && (
                    <Button variant="ghost" size="sm" onClick={() => flagEvidence(e.id)} disabled={flagging} data-testid={`flag-evidence-${e.id}`} className="text-amber-700 hover:bg-amber-50">
                      <Flag className="mr-1 h-3 w-3" strokeWidth={1.75} />
                      Flag
                    </Button>
                  )}
                  {e.flagged && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">Flagged</Badge>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Contradicting findings */}
      <section className="space-y-3" data-testid="finding-contradicting-section">
        <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">
          <AlertTriangle className="mr-1 inline h-4 w-4 text-rose-600" strokeWidth={2} />
          {t("findings:detail.contradictingTitle")}
        </h2>
        {(data.contradicting_findings || []).length === 0 ? (
          <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500">{t("findings:detail.noContradicting")}</p>
        ) : (
          <ul className="space-y-2">
            {data.contradicting_findings.map((c) => (
              <li key={c.id}>
                <Link to={`/findings/${c.id}`} className="block rounded-xl border border-rose-200 bg-rose-50/40 p-4 transition-colors hover:bg-rose-50" data-testid={`contradicting-finding-${c.id}`}>
                  <p className="text-sm text-rose-900">{c.text}</p>
                  <p className="mt-1 text-[11px] text-rose-700/80">{c.theme_label} · {Math.round((c.confidence || 0) * 100)}%</p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
